/**
 * All widget styles as a single CSS string.
 * Injected into the page via a <style> tag to avoid external CSS dependencies.
 * All selectors scoped under #fh-widget-root to prevent host app conflicts.
 */
export const WIDGET_CSS = `
  #fh-widget-root {
    --fh-primary: #6366f1;
    --fh-primary-hover: #4f46e5;
    --fh-bg: #ffffff;
    --fh-bg-secondary: #f8fafc;
    --fh-text: #1e293b;
    --fh-text-secondary: #64748b;
    --fh-border: #e2e8f0;
    --fh-danger: #ef4444;
    --fh-success: #22c55e;
    --fh-radius: 12px;
    --fh-shadow: 0 20px 60px rgba(0,0,0,0.15), 0 4px 16px rgba(0,0,0,0.08);
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    font-size: 14px;
    line-height: 1.5;
    color: var(--fh-text);
    box-sizing: border-box;
  }

  #fh-widget-root *, #fh-widget-root *::before, #fh-widget-root *::after {
    box-sizing: border-box;
  }

  /* ── Floating trigger button ─────────────────────────────────────── */
  .fh-trigger-btn {
    position: fixed;
    z-index: 2147483640;
    width: 56px;
    height: 56px;
    border-radius: 50%;
    border: none;
    background: var(--fh-primary);
    color: white;
    cursor: pointer;
    box-shadow: 0 4px 16px rgba(99,102,241,0.4);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s, box-shadow 0.2s;
  }
  .fh-trigger-btn:hover { transform: scale(1.08); box-shadow: 0 6px 24px rgba(99,102,241,0.5); }
  .fh-trigger-btn svg { width: 24px; height: 24px; fill: currentColor; }
  .fh-trigger-btn.bottom-right { bottom: 24px; right: 24px; }
  .fh-trigger-btn.bottom-left  { bottom: 24px; left: 24px; }
  .fh-trigger-btn.top-right    { top: 24px; right: 24px; }
  .fh-trigger-btn.top-left     { top: 24px; left: 24px; }

  /* ── Modal backdrop ──────────────────────────────────────────────── */
  .fh-backdrop {
    position: fixed;
    inset: 0;
    z-index: 2147483641;
    background: rgba(0,0,0,0.4);
    animation: fh-fade-in 0.2s ease;
  }

  @keyframes fh-fade-in {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @keyframes fh-slide-up {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }

  /* ── Modal panel ─────────────────────────────────────────────────── */
  .fh-modal {
    position: fixed;
    z-index: 2147483642;
    bottom: 90px;
    right: 24px;
    width: 420px;
    max-height: calc(100vh - 120px);
    background: var(--fh-bg);
    border-radius: var(--fh-radius);
    box-shadow: var(--fh-shadow);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    animation: fh-slide-up 0.25s ease;
  }
  .fh-modal.bottom-left  { right: auto; left: 24px; }
  .fh-modal.top-right    { bottom: auto; top: 90px; }
  .fh-modal.top-left     { bottom: auto; top: 90px; right: auto; left: 24px; }

  .fh-modal-header {
    padding: 16px 20px;
    background: var(--fh-primary);
    color: white;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .fh-modal-header h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
  }
  .fh-modal-close {
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    font-size: 20px;
    padding: 0 4px;
    opacity: 0.8;
  }
  .fh-modal-close:hover { opacity: 1; }

  .fh-modal-body {
    padding: 20px;
    overflow-y: auto;
    flex: 1;
  }

  /* ── Minimized modal state ───────────────────────────────────────── */
  .fh-modal.minimized {
    max-height: 60px;
    width: 300px;
  }
  .fh-modal.minimized .fh-modal-body {
    display: none;
  }
  .fh-modal-header .fh-minimize-btn {
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    font-size: 20px;
    padding: 0 4px;
    opacity: 0.8;
  }
  .fh-modal-header .fh-minimize-btn:hover { opacity: 1; }
  .fh-modal-header .fh-restore-btn {
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    font-size: 20px;
    padding: 0 4px;
    opacity: 0.8;
  }
  .fh-modal-header .fh-restore-btn:hover { opacity: 1; }

  /* ── Capture buttons row ─────────────────────────────────────────── */
  .fh-capture-row {
    display: flex;
    gap: 10px;
    margin-bottom: 16px;
  }

  .fh-capture-btn {
    flex: 1;
    padding: 12px;
    border: 2px dashed var(--fh-border);
    border-radius: 8px;
    background: var(--fh-bg-secondary);
    cursor: pointer;
    text-align: center;
    transition: border-color 0.2s, background 0.2s;
    font-size: 13px;
    color: var(--fh-text-secondary);
  }
  .fh-capture-btn:hover { border-color: var(--fh-primary); background: #eef2ff; }
  .fh-capture-btn.active { border-color: var(--fh-primary); border-style: solid; background: #eef2ff; color: var(--fh-primary); }
  .fh-capture-btn.disabled { opacity: 0.4; cursor: not-allowed; }
  .fh-capture-btn .fh-capture-icon { font-size: 24px; display: block; margin-bottom: 4px; }

  /* ── Recording indicator ─────────────────────────────────────────── */
  .fh-recording-bar {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 2147483646;
    background: var(--fh-danger);
    color: white;
    padding: 8px 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    font-size: 14px;
    font-weight: 500;
    animation: fh-fade-in 0.2s ease;
  }
  .fh-recording-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: white;
    animation: fh-pulse 1s infinite;
  }
  @keyframes fh-pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.3; }
  }
  .fh-recording-stop {
    padding: 4px 14px;
    border: 1px solid rgba(255,255,255,0.6);
    border-radius: 4px;
    background: transparent;
    color: white;
    cursor: pointer;
    font-size: 13px;
  }

  /* ── Capture preview ─────────────────────────────────────────────── */
  .fh-preview {
    margin-bottom: 16px;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid var(--fh-border);
    position: relative;
  }
  .fh-preview img, .fh-preview video {
    width: 100%;
    display: block;
  }
  .fh-preview-remove {
    position: absolute;
    top: 8px;
    right: 8px;
    background: rgba(0,0,0,0.6);
    color: white;
    border: none;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    cursor: pointer;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  /* ── Form fields ─────────────────────────────────────────────────── */
  .fh-field {
    margin-bottom: 14px;
  }
  .fh-field label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 4px;
    color: var(--fh-text);
  }
  .fh-field input,
  .fh-field textarea,
  .fh-field select {
    width: 100%;
    padding: 8px 12px;
    border: 1px solid var(--fh-border);
    border-radius: 6px;
    font-size: 14px;
    font-family: inherit;
    color: var(--fh-text);
    background: var(--fh-bg);
    transition: border-color 0.2s;
  }
  .fh-field input:focus,
  .fh-field textarea:focus,
  .fh-field select:focus {
    outline: none;
    border-color: var(--fh-primary);
    box-shadow: 0 0 0 3px rgba(99,102,241,0.1);
  }
  .fh-field textarea { resize: vertical; min-height: 80px; }

  .fh-field-row {
    display: flex;
    gap: 10px;
  }
  .fh-field-row .fh-field { flex: 1; }

  /* ── Submit button ───────────────────────────────────────────────── */
  .fh-submit-btn {
    width: 100%;
    padding: 12px;
    border: none;
    border-radius: 8px;
    background: var(--fh-primary);
    color: white;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.2s;
  }
  .fh-submit-btn:hover { background: var(--fh-primary-hover); }
  .fh-submit-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  /* ── Success message ─────────────────────────────────────────────── */
  .fh-success {
    text-align: center;
    padding: 40px 20px;
  }
  .fh-success-icon {
    font-size: 48px;
    margin-bottom: 12px;
  }
  .fh-success h4 {
    margin: 0 0 8px;
    font-size: 18px;
    color: var(--fh-text);
  }
  .fh-success p {
    margin: 0;
    color: var(--fh-text-secondary);
  }

  /* ── Conversational bot UI ──────────────────────────────────────── */
  .fh-bot-container {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .fh-bot-privacy {
    margin: 0;
    font-size: 12px;
    color: var(--fh-text-secondary);
  }

  .fh-bot-thread {
    border: 1px solid var(--fh-border);
    border-radius: 10px;
    background: var(--fh-bg-secondary);
    padding: 10px;
    max-height: 260px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .fh-bot-message {
    max-width: 85%;
    padding: 8px 10px;
    border-radius: 10px;
    font-size: 13px;
    white-space: pre-wrap;
    word-break: break-word;
  }

  .fh-bot-message.user {
    align-self: flex-end;
    background: var(--fh-primary);
    color: #fff;
  }

  .fh-bot-message.assistant {
    align-self: flex-start;
    background: #fff;
    border: 1px solid var(--fh-border);
    color: var(--fh-text);
  }

  .fh-bot-capture-preview {
    max-width: 85%;
    border: 1px solid var(--fh-border);
    border-radius: 8px;
    align-self: flex-end;
  }

  .fh-bot-input-row {
    display: flex;
    gap: 8px;
    align-items: flex-end;
  }

  .fh-bot-input {
    flex: 1;
    min-height: 56px;
    resize: vertical;
    border: 1px solid var(--fh-border);
    border-radius: 8px;
    padding: 8px 10px;
    font-family: inherit;
  }

  .fh-bot-send {
    width: auto;
    min-width: 88px;
  }

  .fh-bot-actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
  }

  .fh-bot-actions .fh-capture-btn,
  .fh-bot-actions .fh-submit-btn {
    margin: 0;
    width: auto;
    flex: 1;
    min-width: 120px;
  }

  .fh-bot-fallback {
    display: none;
  }

  .fh-bot-fallback-visible {
    display: inline-block;
  }

  .fh-bot-status {
    font-size: 12px;
    color: var(--fh-text-secondary);
    min-height: 16px;
  }

  /* ── Responsive ──────────────────────────────────────────────────── */
  @media (max-width: 1024px) {
    .fh-modal {
      width: min(420px, calc(100vw - 32px));
      max-height: calc(100vh - 110px);
    }

    .fh-modal.bottom-left,
    .fh-modal.top-left {
      left: 16px;
    }

    .fh-modal.bottom-right,
    .fh-modal.top-right {
      right: 16px;
    }
  }

  @media (max-width: 768px) {
    .fh-trigger-btn.bottom-right { bottom: 16px; right: 16px; }
    .fh-trigger-btn.bottom-left  { bottom: 16px; left: 16px; }
    .fh-trigger-btn.top-right    { top: 16px; right: 16px; }
    .fh-trigger-btn.top-left     { top: 16px; left: 16px; }

    .fh-modal {
      width: calc(100vw - 24px);
      right: 12px;
      left: 12px;
      bottom: 84px;
      max-height: calc(100vh - 104px);
    }

    .fh-modal.bottom-left,
    .fh-modal.bottom-right,
    .fh-modal.top-left,
    .fh-modal.top-right {
      left: 12px;
      right: 12px;
    }

    .fh-modal.top-left,
    .fh-modal.top-right {
      top: 84px;
      bottom: auto;
    }

    .fh-modal-body {
      padding: 16px;
    }

    .fh-field-row {
      flex-direction: column;
      gap: 0;
    }

    .fh-bot-input-row {
      flex-direction: column;
      align-items: stretch;
    }

    .fh-bot-send {
      width: 100%;
      min-width: 0;
    }
  }

  @media (max-width: 480px) {
    .fh-trigger-btn.bottom-right { bottom: 12px; right: 12px; }
    .fh-trigger-btn.bottom-left  { bottom: 12px; left: 12px; }
    .fh-trigger-btn.top-right    { top: 12px; right: 12px; }
    .fh-trigger-btn.top-left     { top: 12px; left: 12px; }

    .fh-modal {
      width: calc(100vw - 16px);
      right: 8px;
      left: 8px;
      bottom: 80px;
      max-height: calc(100vh - 100px);
    }

    .fh-modal.top-left,
    .fh-modal.top-right {
      top: 80px;
    }
  }
`;
